// src/hooks/usePets.js - FIXED VERSION - Compatible with Backend API
import { useState, useEffect, useCallback } from 'react'
import { petAPI } from '../services/api'
import { showSuccessToast, showErrorToast } from '../utils/notificationUtils'

export const usePets = () => {
  const [pets, setPets] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [refreshing, setRefreshing] = useState(false)

  // Transform backend pet data to frontend format
  const transformPetData = useCallback((backendPet) => {
    // Handle missing data gracefully
    if (!backendPet) return null
    const determineHealthStatus = (pet) => {
      if (pet.special_notes?.toLowerCase().includes('sick') ||
        pet.special_notes?.toLowerCase().includes('checkup') ||
        pet.allergies?.length > 0) {
        return "Needs Checkup"
      }
      if (pet.special_notes?.toLowerCase().includes('medication') ||
        pet.special_notes?.toLowerCase().includes('treatment')) {
        return "Under Treatment"
      }
      return "Healthy"
    }

    const getHealthColor = (status) => {
      switch (status) {
        case "Healthy":
          return "#8548A8"
        case "Needs Checkup":
          return "#D65745"
        case "Under Treatment":
          return "#F59E0B"
        default:
          return "#8548A8"
      }
    }

    // Handle nested species and breed objects
    const getSpeciesName = (species) => {
      if (typeof species === 'string') return species
      if (species && species.name) return species.name
      return 'Unknown'
    }

    const getBreedName = (breed) => {
      if (typeof breed === 'string') return breed
      if (breed && breed.name) return breed.name
      return 'Mixed'
    }

    // Handle profile image - supports string path or object with url/path fields
    const getProfileImage = (profileImagePath) => {
      if (!profileImagePath) return "/images/no-image-pet.png"

      // If backend sends an object, try common URL keys
      if (typeof profileImagePath === 'object') {
        if (Object.keys(profileImagePath).length === 0) {
          return "/images/no-image-pet.png"
        }

        const candidate =
          profileImagePath.url ||
          profileImagePath.path ||
          profileImagePath.full_url ||
          profileImagePath.fullPath

        if (typeof candidate === 'string' && candidate.trim()) {
          return candidate
        }

        return "/images/no-image-pet.png"
      }

      // Plain string path
      if (typeof profileImagePath === 'string' && profileImagePath.trim()) {
        return profileImagePath
      }

      return "/images/no-image-pet.png"
    }

    // Handle gallery images - ensure it's an array
    const getGalleryImages = (galleryImages) => {
      if (!galleryImages) return []
      if (Array.isArray(galleryImages)) return galleryImages
      return []
    }

    const healthStatus = determineHealthStatus(backendPet)
    return {
      id: backendPet.id,
      name: backendPet.name || 'Unnamed Pet',
      species: getSpeciesName(backendPet.species),
      breed: getBreedName(backendPet.breed),
      age: backendPet.age_formatted || `${backendPet.age || 0} years`,
      health: healthStatus,
      healthColor: getHealthColor(healthStatus),
      image: getProfileImage(backendPet.profile_image_path),
      color: backendPet.color,
      provider: backendPet.provider_name,
      providerNumber: backendPet.provider_number,
      policyNumber: backendPet.policy_number,
      gender: backendPet.gender,
      microchipId: backendPet.microchip_id,
      smartCollarId: backendPet.smart_collar_id,
      weight: backendPet.weight ? `${backendPet.weight} kg` : 'Not specified',
      sizeCategory: backendPet.size_category,
      spayedNeutered: backendPet.is_spayed_neutered ? "Yes" : "No",
      birthDate: backendPet.birth_date,
      adoptionDate: backendPet.adoption_date,
      adopted_rescued: backendPet.adopted_rescued || null,
      bio: backendPet.bio,
      specialNotes: backendPet.special_notes,
      allergies: backendPet.allergies || [],
      lastKnownLocation: backendPet.last_known_location,
      isLocationTrackingEnabled: backendPet.is_location_tracking_enabled,
      isPublicProfile: backendPet.is_public_profile,
      allowSocialInteractions: backendPet.allow_social_interactions,
      galleryImages: getGalleryImages(backendPet.gallery_images),
      createdAt: backendPet.created_at,
      updatedAt: backendPet.updated_at,
      // Additional fields from the new structure
      owner: backendPet.owner,
      ownerId: backendPet.owner_id || backendPet.user_id || backendPet.owner?.id || null,
      userId: backendPet.user_id || backendPet.owner_id || backendPet.owner?.id || null,
      viewerIsOwner: !!backendPet.viewer_is_owner,
      primaryImageUrl: backendPet.primary_image_url,
      adoptionAge: backendPet.adoption_age,
      timeSinceAdoption: backendPet.time_since_adoption,
      guardians: backendPet.guardians || []
    }
  }, [])

  // FIXED: Fetch pets from API with better error handling
  const fetchPets = useCallback(async (showToast = false) => {
    try {
      if (showToast) {
        setRefreshing(true)
      } else {
        setLoading(true)
      }
      setError(null)

      // FIXED: Use the correct API method with proper error handling
      const response = await petAPI.getMyPets()

      // FIXED: Handle Laravel response structure
      let petsData = []

      if (response.data) {
        // Handle Laravel success response
        if (response.data.status === 'success' && response.data.data) {
          // Paginated response
          if (response.data.data.data && Array.isArray(response.data.data.data)) {
            petsData = response.data.data.data
          }
          // Direct array response
          else if (Array.isArray(response.data.data)) {
            petsData = response.data.data
          }
        }
        // Handle direct data response
        else if (Array.isArray(response.data)) {
          petsData = response.data
        }
        // Handle other success indicators
        else if (response.data.success && response.data.data) {
          petsData = Array.isArray(response.data.data) ? response.data.data : [response.data.data]
        }
      }

      const transformedPets = petsData
        .map(transformPetData)
        .filter(pet => pet !== null) // Remove any failed transformations

      setPets(transformedPets)

      if (showToast) {
        showSuccessToast(`${transformedPets.length} pets loaded successfully`)
      }

    } catch (error) {
      console.error('❌ Error fetching pets:', error)

      // FIXED: Better error message extraction for Laravel responses
      let errorMessage = 'Failed to load pets'

      if (error.response?.data) {
        const errorData = error.response.data
        if (errorData.message) {
          errorMessage = errorData.message
        } else if (errorData.error) {
          errorMessage = errorData.error
        } else if (errorData.errors) {
          // Handle validation errors
          const firstError = Object.values(errorData.errors)[0]
          errorMessage = Array.isArray(firstError) ? firstError[0] : firstError
        }
      } else if (error.message) {
        errorMessage = error.message
      }

      // Handle specific error types
      if (error.response?.status === 401) {
        errorMessage = 'Authentication required. Please log in again.'
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to view pets.'
      } else if (error.response?.status === 404) {
        errorMessage = 'Pets endpoint not found. Please check API configuration.'
      }

      setError(errorMessage)

      if (showToast) {
        showErrorToast(errorMessage)
      }

    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [transformPetData])

  // FIXED: Create a new pet with better response handling
  const createPet = useCallback(async (petData) => {
    try {
      const response = await petAPI.createPet(petData)

      // FIXED: Handle Laravel response structure
      let petDataResult = null

      if (response.data) {
        if (response.data.status === 'success' && response.data.data) {
          petDataResult = response.data.data
        } else if (response.data.success && response.data.data) {
          petDataResult = response.data.data
        } else if (response.data.id) {
          petDataResult = response.data
        }
      }

      if (petDataResult) {
        const newPet = transformPetData(petDataResult)

        if (newPet) {
          setPets(prev => [...prev, newPet])
          showSuccessToast(`${newPet.name} was added successfully!`)
          return { success: true, data: newPet }
        }
      }

      throw new Error(response.data?.message || 'Failed to create pet')

    } catch (error) {
      console.error('❌ Error creating pet:', error)

      let errorMessage = 'Failed to create pet'
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.response?.data?.errors) {
        const firstError = Object.values(error.response.data.errors)[0]
        errorMessage = Array.isArray(firstError) ? firstError[0] : firstError
      } else if (error.message) {
        errorMessage = error.message
      }

      showErrorToast(errorMessage)
      return { success: false, error: errorMessage }
    }
  }, [transformPetData])

  // FIXED: Update a pet with better response handling
  const updatePet = useCallback(async (petId, petData) => {
    try {
      const response = await petAPI.updatePet(petId, petData)

      // FIXED: Handle Laravel response structure
      let petDataResult = null

      if (response.data) {
        if (response.data.status === 'success' && response.data.data) {
          petDataResult = response.data.data
        } else if (response.data.success && response.data.data) {
          petDataResult = response.data.data
        } else if (response.data.id) {
          petDataResult = response.data
        }
      }

      if (petDataResult) {
        const updatedPet = transformPetData(petDataResult)

        if (updatedPet) {
          setPets(prev => prev.map(pet => pet.id === petId ? updatedPet : pet))
          showSuccessToast(`${updatedPet.name} was updated successfully!`)
          return { success: true, data: updatedPet }
        }
      }

      throw new Error(response.data?.message || 'Failed to update pet')

    } catch (error) {
      console.error('❌ Error updating pet:', error)

      let errorMessage = 'Failed to update pet'
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.response?.data?.errors) {
        const firstError = Object.values(error.response.data.errors)[0]
        errorMessage = Array.isArray(firstError) ? firstError[0] : firstError
      } else if (error.message) {
        errorMessage = error.message
      }

      showErrorToast(errorMessage)
      return { success: false, error: errorMessage }
    }
  }, [transformPetData])

  // FIXED: Delete a pet with better response handling
  const deletePet = useCallback(async (petId) => {
    try {
      const response = await petAPI.deletePet(petId)

      // FIXED: Handle successful deletion (204 No Content or success response)
      if (response.status === 204 ||
        response.data?.status === 'success' ||
        response.data?.success) {

        setPets(prev => prev.filter(pet => pet.id !== petId))
        showSuccessToast('Pet deleted successfully!')
        return { success: true }
      } else {
        throw new Error(response.data?.message || 'Failed to delete pet')
      }

    } catch (error) {
      console.error('❌ Error deleting pet:', error)

      let errorMessage = 'Failed to delete pet'
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }

      showErrorToast(errorMessage)
      return { success: false, error: errorMessage }
    }
  }, [])

  // FIXED: Get a single pet by ID with better response handling
  const getPet = useCallback(async (petId) => {
    try {
      const response = await petAPI.getPet(petId)

      // FIXED: Handle Laravel response structure
      let petDataResult = null

      if (response.data) {
        if (response.data.status === 'success' && response.data.data) {
          petDataResult = response.data.data
        } else if (response.data.success && response.data.data) {
          petDataResult = response.data.data
        } else if (response.data.id) {
          petDataResult = response.data
        }
      }

      if (petDataResult) {
        const transformedPet = transformPetData(petDataResult)

        if (transformedPet) {
          return { success: true, data: transformedPet }
        } else {
          throw new Error('Failed to transform pet data')
        }
      } else {
        throw new Error(response.data?.message || 'Pet not found')
      }

    } catch (error) {
      console.error('❌ Error fetching pet:', error)

      let errorMessage = 'Failed to fetch pet'
      if (error.response?.status === 404) {
        errorMessage = 'Pet not found'
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }

      return { success: false, error: errorMessage }
    }
  }, [transformPetData])

  // FIXED: Upload pet profile image with better response handling
  const uploadProfileImage = useCallback(async (petId, file) => {
    try {
      const response = await petAPI.uploadProfileImage(petId, file)

      // FIXED: Handle Laravel response structure
      let imageData = null

      if (response.data) {
        if (response.data.status === 'success' && response.data.data) {
          imageData = response.data.data
        } else if (response.data.success && response.data.data) {
          imageData = response.data.data
        } else if (response.data.profile_image_path) {
          imageData = response.data
        }
      }

      if (imageData) {
        // Update the pet's image in local state
        setPets(prev => prev.map(pet =>
          pet.id === petId
            ? {
              ...pet,
              image: imageData.profile_image_path
                ? `${imageData.profile_image_path}`
                : pet.image
            }
            : pet
        ))
        showSuccessToast('Profile image updated successfully!')
        return { success: true, data: imageData }
      } else {
        throw new Error(response.data?.message || 'Failed to upload image')
      }

    } catch (error) {
      console.error('❌ Error uploading image:', error)

      let errorMessage = 'Failed to upload image'
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.response?.data?.errors) {
        const firstError = Object.values(error.response.data.errors)[0]
        errorMessage = Array.isArray(firstError) ? firstError[0] : firstError
      } else if (error.message) {
        errorMessage = error.message
      }

      showErrorToast(errorMessage)
      return { success: false, error: errorMessage }
    }
  }, [])

  // Search pets
  const searchPets = useCallback((query) => {
    if (!query.trim()) {
      return pets
    }

    const lowercaseQuery = query.toLowerCase()
    return pets.filter(pet =>
      pet.name?.toLowerCase().includes(lowercaseQuery) ||
      pet.species?.toLowerCase().includes(lowercaseQuery) ||
      pet.breed?.toLowerCase().includes(lowercaseQuery) ||
      pet.color?.toLowerCase().includes(lowercaseQuery)
    )
  }, [pets])

  // Filter pets by criteria
  const filterPets = useCallback((filters) => {
    let filteredPets = [...pets]

    if (filters.species) {
      filteredPets = filteredPets.filter(pet =>
        pet.species?.toLowerCase() === filters.species.toLowerCase()
      )
    }

    if (filters.health) {
      filteredPets = filteredPets.filter(pet =>
        pet.health?.toLowerCase() === filters.health.toLowerCase()
      )
    }

    if (filters.sizeCategory) {
      filteredPets = filteredPets.filter(pet =>
        pet.sizeCategory?.toLowerCase() === filters.sizeCategory.toLowerCase()
      )
    }

    if (filters.gender) {
      filteredPets = filteredPets.filter(pet =>
        pet.gender?.toLowerCase() === filters.gender.toLowerCase()
      )
    }

    return filteredPets
  }, [pets])

  // Get pets statistics
  const getPetsStats = useCallback(() => {
    const stats = {
      total: pets.length,
      healthy: pets.filter(pet => pet.health === 'Healthy').length,
      needsCheckup: pets.filter(pet => pet.health === 'Needs Checkup').length,
      underTreatment: pets.filter(pet => pet.health === 'Under Treatment').length,
      bySpecies: {},
      byGender: {},
      bySizeCategory: {}
    }

    pets.forEach(pet => {
      // Count by species
      if (pet.species) {
        stats.bySpecies[pet.species] = (stats.bySpecies[pet.species] || 0) + 1
      }

      // Count by gender
      if (pet.gender) {
        stats.byGender[pet.gender] = (stats.byGender[pet.gender] || 0) + 1
      }

      // Count by size category
      if (pet.sizeCategory) {
        stats.bySizeCategory[pet.sizeCategory] = (stats.bySizeCategory[pet.sizeCategory] || 0) + 1
      }
    })

    return stats
  }, [pets])

  // Get complete medical history for a pet
  const getCompleteMedicalHistory = useCallback(async (petId) => {
    try {

      const response = await petAPI.getCompleteMedicalHistory(petId, {
        limit: 100,
        per_page: 100,
        simple: true
      })


      if (response.data) {
        return { success: true, data: response.data }
      } else {
        throw new Error('No medical history data received')
      }

    } catch (error) {
      console.error('❌ Error fetching medical history:', error)

      let errorMessage = 'Failed to fetch medical history'
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.response?.data?.errors) {
        const firstError = Object.values(error.response.data.errors)[0]
        errorMessage = Array.isArray(firstError) ? firstError[0] : firstError
      } else if (error.message) {
        errorMessage = error.message
      }

      showErrorToast(errorMessage)
      return { success: false, error: errorMessage }
    }
  }, [])

  // FIXED: Initial fetch on mount with better error handling
  useEffect(() => {
    // Only fetch if we have authentication
    const token = localStorage.getItem('access_token')
    if (token) {
      fetchPets()
    } else {
      console.warn('⚠️ No authentication token found, skipping pets fetch')
      setLoading(false)
    }
  }, [fetchPets])

  // Refresh function for pull-to-refresh functionality
  const refresh = useCallback(() => {
    fetchPets(true)
  }, [fetchPets])

  return {
    // Data
    pets,
    loading,
    error,
    refreshing,

    // Actions
    fetchPets,
    createPet,
    updatePet,
    deletePet,
    getPet,
    uploadProfileImage,
    refresh,
    getCompleteMedicalHistory,

    // Utility functions
    searchPets,
    filterPets,
    getPetsStats,

    // Computed values
    isEmpty: pets.length === 0,
    hasError: !!error,

    // Debug info
    debugInfo: {
      hasToken: !!localStorage.getItem('access_token'),
      petsCount: pets.length,
      lastError: error
    }
  }
}